import { DeleteFilled, EditFilled, PlusOutlined } from '@ant-design/icons';
import {
  DatePicker,
  Form,
  Button,
  Input,
  message,
  Row,
  Col,
  Select,
  Table,
  Upload,
  Modal,
  FormInstance,
  Popconfirm,
  Image,
  Radio,
} from 'antd';
import type { RcFile, UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import TextArea from 'antd/es/input/TextArea';
import type { ColumnsType } from 'antd/es/table';
import React, { SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as input from '../../Component/Layouts/FormInput.Style';
import { DoctorsType } from '../../Pages/Prescription/InterfaceType';
import EditProductModal from '../Modal/EditProductModal';
import SearchProductModal from '../Modal/SearchProductModal';
import { DataContext } from '../../Pages/Prescription/PrescriptionForm';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { DELIVERY_CONSTANTS } from '../../Utils/constants';
import { checkStock } from '../../Services/Product.service';
import { getSubAccount } from '../../Services/Prescription.service';

const PresrciptionDetailForm = ({
  form,
  onFinish,
  deliveryType,
  itemOnHandBalance,
  setListImage,
  listImage,
  setCheckItemList,
}: {
  form: FormInstance;
  onFinish: any;
  deliveryType: any;
  itemOnHandBalance: any[];
  setListImage: React.Dispatch<SetStateAction<string[]>>;
  setCheckItemList: React.Dispatch<SetStateAction<any[]>>;
  listImage: string[];
}) => {
  const paramId = useParams();
  const formatDate = 'YYYY-MM-DD';
  const { prescriptData, initialData } = useContext(DataContext);

  const [isOpenSearchProduct, setIsOpenSearchProduct] = useState(false);
  const [isOpenEditProduct, setIsOpenEditProduct] = useState(false);
  const [itemList, setItemList] = useState<any[]>([]);
  const [item, setItem] = useState<any>();
  const [totalPrice, setTotalPrice] = useState(0);
  const [deliveryTypeTmp, setDeliveryTypeTmp] = useState('');
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [disableForm, setDisableForm] = useState(false);
  const [attachmentList, setAttachmentList] = useState<any>([]);
  const [subAccount, setSubAccount] = useState<string[]>([]);
  const { Option } = Select;
  const currentLabelLanguage = Form.useWatch('label_language', form) || 'th';
  const ITEM_LIST_SESSION_KEY = useMemo(() => `prescription_item_list_draft_${paramId?.id || 'new'}`, [paramId?.id]);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const res = await getSubAccount();
    setSubAccount(res.sub_account);
  };

  useEffect(() => {
    if ((subAccount?.length ?? 0) === 1 && !form.getFieldValue('sub_account')) {
      form.setFieldsValue({ sub_account: subAccount?.[0] });
    }
  }, [subAccount, form]);

  useEffect(() => {
    if (prescriptData === undefined) {
      return;
    }

    if (prescriptData.prescript_info.display_type === 'VIEW') {
      setDisableForm(true);
    } else if (prescriptData.prescript_info.display_type === 'EDIT') {
      setDisableForm(false);
    }

    const draft = sessionStorage.getItem(ITEM_LIST_SESSION_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);

        if (Array.isArray(parsed) && parsed.length > 0) {
          setItemList(parsed);
        } else {
          setItemList(prescriptData.prescript_info.detail || []);
        }
      } catch (error) {
        console.error('Failed to parse itemList draft from sessionStorage:', error);
        setItemList(prescriptData.prescript_info.detail || []);
      }
    } else {
      setItemList(prescriptData.prescript_info.detail || []);
    }

    const mapUrl = (prescriptData.prescript_info.attachment || []).map((url: string, index: number) => {
      const cleanUrl = url.split('?')[0];
      const fileName =
        decodeURIComponent(cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1)) || `attachment-${index + 1}`;

      return {
        uid: `attachment-${index}`,
        name: fileName,
        status: 'done',
        url,
        type: /\.pdf$/i.test(cleanUrl) ? 'application/pdf' : undefined,
      };
    });

    setAttachmentList(mapUrl);
    setAttachmentList(mapUrl);
    setDeliveryTypeTmp(prescriptData.delivery_info.type);
    setListImage(prescriptData.prescript_info.attachment || []);

    form.setFieldsValue({
      doctor_id: prescriptData.prescript_info.doctor_id,
      prescript_date: dayjs(prescriptData.prescript_info.date, formatDate),
      submit_date: dayjs(prescriptData.created_date, formatDate),
      diagnosis: prescriptData.prescript_info.diagnosis,
      prescript_no: prescriptData.prescript_info.no,
      symptom: prescriptData.prescript_info.symptom,
      total_price: prescriptData.prescript_info.total_price,
      sub_total_price: prescriptData.prescript_info.sub_total_price,
      doctor_name: prescriptData.prescript_info.doctor_name,
      sub_account: prescriptData.prescript_info.sub_account,
      isApi: prescriptData.created_by_source === 'PARTNER-API',
    });
  }, [prescriptData, initialData, form, setListImage, ITEM_LIST_SESSION_KEY]);

  useEffect(() => {
    const raw = initialData?.label_language;
    if (raw == null) return;

    const normalized = String(raw).trim().toLowerCase();
    form.setFieldsValue({
      label_language: normalized === 'en' ? 'en' : 'th',
    });
  }, [form, initialData?.label_language]);

  useEffect(() => {
    if (deliveryType) {
      setDeliveryTypeTmp(deliveryType);
    }
  }, [deliveryType]);

  useEffect(() => {
    const validItemOnHandBalance = Array.isArray(itemOnHandBalance) ? itemOnHandBalance : [];

    setItemList((prevList) =>
      prevList.map((currentItem) => {
        if (deliveryType !== DELIVERY_CONSTANTS.DELIVERY.PICK_UP) {
          return {
            ...currentItem,
            on_hand_balance: '-',
          };
        }

        const matchingBalance = validItemOnHandBalance.find((balance) => balance.itm_id === currentItem.item_code);

        return {
          ...currentItem,
          on_hand_balance: matchingBalance?.onhand_balance_qty || 0,
        };
      }),
    );
  }, [itemOnHandBalance, deliveryType]);

  useEffect(() => {
    sessionStorage.setItem(ITEM_LIST_SESSION_KEY, JSON.stringify(itemList));
  }, [itemList, ITEM_LIST_SESSION_KEY]);

  useEffect(() => {
    let nextTotalPrice = 0;

    form.setFieldsValue({
      detail: itemList,
    });

    if (itemList.length > 0) {
      itemList.forEach((obj) => {
        nextTotalPrice += Number(obj.item_total_price) || 0;
      });
    }

    setTotalPrice(nextTotalPrice);
    setCheckItemList(itemList);
  }, [itemList, form, setCheckItemList]);

  useEffect(() => {
    let price = 0;

    if (deliveryTypeTmp) {
      if (deliveryTypeTmp === '' || deliveryTypeTmp === '1') {
        price = 0;
      } else if (deliveryTypeTmp === DELIVERY_CONSTANTS.DELIVERY.FAST) {
        if (prescriptData?.prescript_info?.delivery_price != null) {
          price = prescriptData.prescript_info.delivery_price;
        } else if (initialData) {
          price = initialData.shipping_fee ?? 0;
        }
      } else {
        if (prescriptData?.prescript_info?.delivery_price != null) {
          price = prescriptData.prescript_info.delivery_price;
        } else if (initialData) {
          price = initialData.delivery_price;
        }
      }
      setDeliveryPrice(price);
    }
  }, [deliveryTypeTmp, initialData, prescriptData]);

  useEffect(() => {
    handleItemPricing();
  }, [deliveryPrice, totalPrice]);

  useEffect(() => {
    form.setFieldsValue({
      detail: itemList,
    });
  }, [itemList, form]);

  const handleItemPricing = () => {
    form.setFieldsValue({
      delivery_price: deliveryPrice,
      total_price: totalPrice,
      sub_total_price: totalPrice + deliveryPrice,
    });
  };

  const callbackProductModal = async (data: any) => {
    const itemFind = itemList.find((obj) => obj.item_code === data.item_code);

    if (itemFind) {
      message.error('สินค้าอยู่ในรายการแล้ว!');
      return;
    }

    try {
      const selectedDeliverySite = form.getFieldValue('delivery_site');

      let updatedItem = { ...data };

      if (deliveryType) {
        const response = await checkStock({
          item_code: [data.item_code],
          site_id: [selectedDeliverySite],
        });

        if (response?.datas) {
          const stockData = response.datas.find((stock: { itm_id: string }) => stock.itm_id === data.item_code);

          updatedItem = {
            ...data,
            on_hand_balance: stockData?.onhand_balance_qty || 0,
          };
        } else {
          updatedItem = {
            ...data,
            on_hand_balance: 0,
          };
        }
      }

      setItemList((prevList) => [...prevList, updatedItem]);
      message.success('เพิ่มสินค้าเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error checking stock:', error);
      message.error('เกิดข้อผิดพลาดในการตรวจสอบจำนวนสินค้า');
    }
  };

  const openEditItemMocdal = (data: any) => {
    setIsOpenEditProduct(true);
    setItem({ ...data });
  };

  const callbackUpdateItem = (data: any) => {
    const updateItem = itemList.map((obj) => {
      if (obj.item_code === data.item_code) {
        return {
          ...obj,
          ...data,
        };
      }
      return obj;
    });

    setItemList(updateItem);
    setItem(data);
    setIsOpenEditProduct(false);
    message.success('แก้ไขสินค้าเรียบร้อยแล้ว');
  };

  const deleteProduct = (data: any) => {
    const itemFilter = itemList.filter((obj) => obj.item_code !== data.item_code);
    setItemList(itemFilter);
  };

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const isPdfFile = (file: UploadFile) => {
    const raw = String(file.url || file.preview || '').toLowerCase();
    return (
      file.type === 'application/pdf' ||
      file.name?.toLowerCase().endsWith('.pdf') ||
      raw.startsWith('data:application/pdf')
    );
  };

  const dataURLToBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch?.[1] || 'application/octet-stream';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  };

  const handleCancel = () => setPreviewOpen(false);

  const handlePreview = async (file: UploadFile) => {
    const pdf = isPdfFile(file);
    const newTab = pdf ? window.open('', '_blank') : null;

    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }

    const src = (file.url || file.preview) as string;

    if (pdf) {
      if (!newTab) {
        message.error('เบราว์เซอร์บล็อกการเปิดไฟล์ PDF');
        return;
      }

      let pdfUrl = src;

      if (src.startsWith('data:application/pdf')) {
        const blob = dataURLToBlob(src);
        pdfUrl = URL.createObjectURL(blob);
      }

      newTab.location.href = pdfUrl;
      return;
    }

    setPreviewImage(src);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
  };

  const handleChange: UploadProps['onChange'] = () => {
    // intentionally left blank
  };

  const MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10 MB
  const fileListRef = useRef<any[]>([]);

  useEffect(() => {
    fileListRef.current = fileList;
  }, [fileList]);

  const handleBeforeUpload = async (file: any) => {
    const currentTotalSize = fileListRef.current.reduce((sum: number, item: any) => sum + (item?.size || 0), 0);

    const newTotalSize = currentTotalSize + (file?.size || 0);

    if (newTotalSize > MAX_TOTAL_SIZE) {
      message.error('อัปโหลดไฟล์รวมกันได้ไม่เกิน 10 MB');
      return Upload.LIST_IGNORE;
    }
    const url = (await getBase64(file)) as string;
    setAttachmentList([...attachmentList, { url }]);
    setFileList([...fileList, file]);
    return false;
  };

  useEffect(() => {
    form.setFieldsValue({
      attachmentFile: fileList,
    });
  }, [fileList, form]);

  const handleRemove = async (file: any) => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);

    setFileList(newFileList);
    setAttachmentList(attachmentList.filter((url: any) => url.url !== file.url));
    setListImage(listImage.filter((url: any) => url !== file.url));
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 5 }}>เพิ่มแนบไพล์</div>
    </div>
  );

  const getLabelValue = (rowItem: any, field: string) => {
    return rowItem?.label_by_lang?.[currentLabelLanguage]?.[field] ?? rowItem?.[field] ?? '';
  };

  const renderText = (text: any) => (text ? text : '-');

  const renderActionButton = (tableItem: any, onEdit: Function, onDelete: Function) => (
    <div>
      <Row>
        <Col span={12}>
          <Button
            type="primary"
            style={{
              backgroundColor: '#ffc107',
              justifyContent: 'center',
              color: 'white',
            }}
            icon={<EditFilled />}
            onClick={() => onEdit(tableItem)}
          />
        </Col>
        <Col span={12}>
          <Popconfirm
            placement="left"
            title={'ยืนยันการลบรายการ?'}
            onConfirm={() => onDelete(tableItem)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
          >
            <Button
              style={{
                backgroundColor: '#F25F0A',
                justifyContent: 'center',
                color: 'white',
              }}
              icon={<DeleteFilled />}
            />
          </Popconfirm>
        </Col>
      </Row>
    </div>
  );

  const listItemColumn: ColumnsType<any> = [
    {
      title: 'รูป',
      dataIndex: 'item_image',
      align: 'center',
      render: (_, rowItem: any) => (
        <Image
          src={rowItem.item_image}
          alt={rowItem.item_name}
          width={50}
          height={50}
          style={{ objectFit: 'cover' }}
          preview={true}
        />
      ),
    },
    {
      title: 'ชื่อยา',
      align: 'center',
      render: (_, rowItem: any) =>
        renderText(currentLabelLanguage === 'en' ? rowItem.item_name_en || rowItem.item_name : rowItem.item_name),
    },
    {
      title: 'ขนาด',
      dataIndex: 'item_size',
      align: 'center',
      render: renderText,
    },
    {
      title: 'ราคา (บาท)',
      dataIndex: 'item_price',
      align: 'center',
      render: renderText,
    },
    {
      title: 'จำนวน',
      dataIndex: 'item_qty',
      align: 'center',
      render: renderText,
    },
    {
      title: 'สินค้าคงเหลือ',
      dataIndex: 'on_hand_balance',
      align: 'center',
      render: renderText,
    },
    {
      title: 'ราคารวม',
      dataIndex: 'item_total_price',
      align: 'center',
      render: renderText,
    },
    {
      title: 'วิธีใช้/คำแนะนำ',
      align: 'center',
      render: (_: any, rowItem: any) => {
        const method = getLabelValue(rowItem, 'item_method');
        const recommend = getLabelValue(rowItem, 'remark');

        return (
          <div>
            {method && <div>- {method}</div>}
            {recommend && <div>- {recommend}</div>}
          </div>
        );
      },
    },
    {
      title: 'หมายเหตุ',
      align: 'center',
      render: (_: any, rowItem: any) => renderText(getLabelValue(rowItem, 'remark')),
    },
    {
      title: '',
      dataIndex: '',
      align: 'center',
      render: (rowItem: any) => (!disableForm ? renderActionButton(rowItem, openEditItemMocdal, deleteProduct) : null),
    },
  ];

  const onSubmit = (values: any) => {
    const payload = {
      ...values,
      detail: itemList,
    };

    onFinish(payload);
  };

  return (
    <div>
      <Row>
        <input.tittle style={{ fontSize: 20, marginBottom: 20 }}>ข้อมูลใบสั่งแพทย์</input.tittle>
      </Row>

      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        disabled={disableForm}
        initialValues={{ label_language: 'th' }}
      >
        <Form.Item label="ภาษาบนฉลากยา" name="label_language" className="left-align">
          <Radio.Group name="radio_language">
            <Radio value="th">ภาษาไทย</Radio>
            <Radio value="en">English</Radio>
          </Radio.Group>
        </Form.Item>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="clinic" className="left-align" name="sub_account" rules={[{ required: true }]}>
              <Select placeholder="เลือก">
                {(subAccount ?? []).map((status) => (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="เลขที่ใบสั่งแพทย์" name="prescript_no" rules={[{ required: true }]}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="วันที่บนใบสั่งแพทย์" name="prescript_date" rules={[{ required: true }]}>
              <DatePicker style={{ display: 'flex' }} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.isApi !== cur.isApi}>
              {({ getFieldValue }) =>
                getFieldValue('isApi') ? (
                  <>
                    <Form.Item
                      label="แพทย์ผู้วินิจฉัย"
                      className="left-align"
                      name="doctor_name"
                      rules={[{ required: true, message: 'กรุณาระบุชื่อแพทย์' }]}
                    >
                      <Input placeholder="ระบุชื่อแพทย์" />
                    </Form.Item>

                    <Form.Item name="doctor_id" hidden preserve={false}>
                      <Input />
                    </Form.Item>
                  </>
                ) : (
                  <Form.Item
                    label="แพทย์ผู้วินิจฉัย"
                    className="left-align"
                    name="doctor_id"
                    rules={[{ required: true, message: 'กรุณาเลือกแพทย์' }]}
                  >
                    <Select placeholder="เลือก">
                      {(initialData?.doctors ?? []).map((d: DoctorsType) => (
                        <Option key={d.id} value={d.id}>
                          {d.title} {d.first_name} {d.last_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )
              }
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="วันที่ส่งสั่งแพทย์" name="submit_date" rules={[{ required: true }]}>
              <DatePicker style={{ display: 'flex' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item label="อาการ" name="symptom">
              <TextArea showCount style={{ resize: 'none' }} rows={5} maxLength={250} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item label="รายละเอียดการวินิจฉัย" name="diagnosis">
              <TextArea showCount style={{ resize: 'none' }} rows={5} maxLength={250} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={1}>
            <Form.Item>
              <Button
                type="primary"
                style={{ color: '#fff', backgroundColor: '#50B0E9' }}
                onClick={() => setIsOpenSearchProduct(true)}
              >
                ค้นหาสินค้า
              </Button>

              <SearchProductModal
                open={isOpenSearchProduct}
                onCancel={() => setIsOpenSearchProduct(false)}
                onOk={() => setIsOpenSearchProduct(false)}
                onCallback={callbackProductModal}
                validItemList={itemList}
                labelLanguage={currentLabelLanguage}
              />

              <EditProductModal
                item={item}
                onOpen={isOpenEditProduct}
                onCancel={() => setIsOpenEditProduct(false)}
                onCallback={callbackUpdateItem}
                labelLanguage={currentLabelLanguage}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="detail"
              style={{
                display: 'inline-block',
                width: 'calc(100% - 8px)',
              }}
            >
              <Table
                bordered
                rowKey={(record: any) => record.item_code}
                columns={listItemColumn}
                dataSource={itemList}
                pagination={false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}></Col>
          <Col span={4}>
            <Form.Item name="total_price">
              <Input disabled addonBefore="ราคารวม" addonAfter="บาท" style={{ textAlign: 'end' }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="delivery_price">
              <Input disabled addonBefore="ค่าจัดส่ง" addonAfter="บาท" style={{ textAlign: 'end' }} />
            </Form.Item>
            <div style={{ fontSize: 12, color: '#999', textAlign: 'end' }}>
              *ค่าส่งอาจมีการเปลื่ยนแปลงจาก เวลาทำการ และ stock ได้
            </div>
          </Col>
          <Col span={4}>
            <Form.Item name="sub_total_price">
              <Input disabled addonBefore="ราคาสุทธิ" addonAfter="บาท" style={{ textAlign: 'end' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={24} style={{ textAlign: 'start' }}>
            <Form.Item label="Upload ข้อมูล">
              <Form.Item name="attachmentFile" noStyle>
                <Upload
                  listType="picture-card"
                  fileList={attachmentList}
                  onChange={handleChange}
                  onRemove={handleRemove}
                  beforeUpload={handleBeforeUpload}
                  onPreview={handlePreview}
                >
                  {attachmentList.length >= 5 ? null : uploadButton}
                </Upload>
              </Form.Item>
              <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={handleCancel}>
                <img alt="example" style={{ width: '100%' }} src={previewImage} />
              </Modal>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default PresrciptionDetailForm;
